import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCookieConsent } from '../../context/CookieConsentContext';
import { Button } from '../ui/button';

const CookieConsent: React.FC = () => {
  const { showBanner, setConsentGiven } = useCookieConsent();
  const [portalElement, setPortalElement] = useState<HTMLElement | null>(null);

  useEffect(() => {
    // Create a div element for the portal if it doesn't exist
    let el = document.getElementById('cookie-consent-portal');
    if (!el) {
      el = document.createElement('div');
      el.id = 'cookie-consent-portal';
      document.body.appendChild(el);
    }
    setPortalElement(el);

    // Cleanup function to remove the portal element
    return () => {
      const portalEl = document.getElementById('cookie-consent-portal');
      if (portalEl && document.body.contains(portalEl)) {
        document.body.removeChild(portalEl);
      }
    };
  }, []);

  const handleAccept = () => {
    setConsentGiven(true);
  };

  const handleReject = () => {
    setConsentGiven(false);
  };

  // Don't render anything if banner shouldn't be shown or portal element doesn't exist
  if (!showBanner || !portalElement) {
    return null;
  }

  return createPortal(
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-blue-900 text-white p-4 shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="mb-4 md:mb-0 pr-4 text-sm">
            <p className="mb-2">
              <span className="font-semibold">JLS Apps</span> and 2 selected third parties collect personal information as specified in the{' '}
              <a 
                href="/privacy-policy" 
                className="text-blue-400 hover:text-blue-300 underline"
                onClick={(e) => {
                  e.preventDefault();
                  window.open('/privacy-policy', '_self');
                }}
              >
                privacy policy
              </a>{' '}
              and use cookies or similar technologies for technical purposes and, with your consent, for{' '}
              <span className="font-semibold">functionality</span> as specified in the{' '}
              <a 
                href="/cookie-policy" 
                className="text-blue-400 hover:text-blue-300 underline"
                onClick={(e) => {
                  e.preventDefault();
                  window.open('/cookie-policy', '_self');
                }}
              >
                cookie policy
              </a>
              . By using our services, you agree to our{' '}
              <a 
                href="/terms-of-service" 
                className="text-blue-400 hover:text-blue-300 underline"
                onClick={(e) => {
                  e.preventDefault();
                  window.open('/terms-of-service', '_self');
                }}
              >
                terms of service
              </a>
              .
            </p>
            <p>
              You can freely give, deny, or withdraw your consent at any time by accessing the preferences panel. Denying consent may make related features unavailable.
            </p>
            <p className="mt-2">
              Use the "Accept" button to consent. Use the "Reject" button to continue without accepting.
            </p>
          </div>
          <div className="flex space-x-3">
            <Button
              onClick={handleReject}
              variant="outline"
              className="bg-transparent border-white text-white hover:bg-blue-800"
            >
              Reject
            </Button>
            <Button
              onClick={handleAccept}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Accept
            </Button>
          </div>
        </div>
      </div>
    </div>,
    portalElement
  );
};

export default CookieConsent;
